import { environment } from '../../../environments/environment';
import { HomescreenSettings } from './settings.models';

export interface TileDefinition {
  id: string;
  url?: string;
  iconName?: string;
  titleKey?: string;
  modal?: boolean;
  /** Empty or absent means the tile is available to everyone, including logged-out users. */
  access_roles?: string[];
}

const catalogue: readonly TileDefinition[] = environment.tiles;

/**
 * Role names are compared verbatim, matching how the backend spells them in
 * `access_roles` (`employee`, `student`, but also `AZUBI` and
 * `extusers@library.tu-dortmund.de`). No case folding, so a claim that differs in
 * casing will not match.
 */
export const isTileAccessible = (tile: TileDefinition, roles: readonly string[]): boolean =>
  tile.access_roles === undefined ||
  tile.access_roles.length === 0 ||
  tile.access_roles.some(required => roles.includes(required));

const highestPosition = (homescreen: HomescreenSettings): number =>
  Object.values(homescreen).reduce(
    (highest, tile) => (tile.position === null ? highest : Math.max(highest, tile.position)),
    -1,
  );

/**
 * Brings the homescreen in line with what the user may see: tiles that became accessible
 * are appended in catalogue order, tiles that are no longer accessible — or no longer in
 * the catalogue at all — are removed. Existing positions are left untouched, including
 * `null` for tiles the user has deliberately hidden.
 *
 * On a fresh install the homescreen is empty, so this seeds every accessible tile.
 *
 * Returns null when nothing would change, which keeps the caller from dispatching and
 * makes repeated application a fixed point.
 */
export const reconcileHomescreen = (
  homescreen: HomescreenSettings,
  roles: readonly string[],
): HomescreenSettings | null => {
  const accessible = catalogue.filter(tile => isTileAccessible(tile, roles));
  const accessibleIds = new Set(accessible.map(tile => tile.id));

  const next: HomescreenSettings = {};
  let changed = false;

  for (const [id, tile] of Object.entries(homescreen)) {
    if (accessibleIds.has(id)) next[id] = tile;
    else changed = true;
  }

  let position = highestPosition(next) + 1;
  for (const tile of accessible) {
    if (!(tile.id in next)) {
      next[tile.id] = { position: position++ };
      changed = true;
    }
  }

  return changed ? next : null;
};
